import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Save, X, CalendarPlus, Trash2, Plus, Pencil } from "lucide-react";
import api from "@/lib/api";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";

const MONTHS = ["Januari", "Februari", "Maret", "April", "Mei", "Juni", "Juli", "Agustus", "September", "Oktober", "November", "Desember"];
const DAYS = ["Sen", "Sel", "Rab", "Kam", "Jum", "Sab", "Min"];

const fmt = (y, m, d) => `${y}-${String(m + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;

export default function KalenderKonten({ pendingScript, clearPending }) {
  const today = new Date();
  const [cursor, setCursor] = useState({ y: today.getFullYear(), m: today.getMonth() });
  const [entries, setEntries] = useState([]); // list of {id, date, idea, notes}
  const [selDate, setSelDate] = useState(null);
  const [idea, setIdea] = useState("");
  const [notes, setNotes] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);

  const loadEntries = () => api.get("/calendar").then((r) => setEntries(r.data)).catch(() => {});
  useEffect(() => { loadEntries(); }, []);

  const byDate = useMemo(() => {
    const map = {};
    entries.forEach((e) => { (map[e.date] = map[e.date] || []).push(e); });
    return map;
  }, [entries]);

  const dateEntries = selDate ? (byDate[selDate] || []) : [];

  const grid = useMemo(() => {
    const first = new Date(cursor.y, cursor.m, 1);
    let startDay = first.getDay();
    startDay = startDay === 0 ? 6 : startDay - 1;
    const daysInMonth = new Date(cursor.y, cursor.m + 1, 0).getDate();
    const cells = [];
    for (let i = 0; i < startDay; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(d);
    return cells;
  }, [cursor]);

  const resetForm = () => { setIdea(""); setNotes(""); setEditingId(null); };

  const openDate = (d) => {
    if (!d) return;
    const key = fmt(cursor.y, cursor.m, d);
    setSelDate(key);
    resetForm();
    if (pendingScript) {
      setNotes(pendingScript);
      setIdea("");
      setShowForm(true);
      toast.success("Script ditempel. Kasih judul & simpan buat jadwalin di tanggal ini!");
      clearPending();
    } else {
      setShowForm((byDate[key] || []).length === 0);
    }
  };

  const startEdit = (e) => { setEditingId(e.id); setIdea(e.idea); setNotes(e.notes); setShowForm(true); };

  const save = async () => {
    if (!idea.trim() && !notes.trim()) { toast.error("Isi ide atau catatan dulu ya"); return; }
    setSaving(true);
    try {
      if (editingId) {
        await api.put(`/calendar/${editingId}`, { date: selDate, idea, notes });
        toast.success("Rencana diperbarui");
      } else {
        await api.post("/calendar", { date: selDate, idea, notes });
        toast.success("Rencana ditambahkan");
      }
      await loadEntries();
      resetForm();
      setShowForm(false);
    } catch (e) {
      toast.error("Gagal menyimpan. Coba lagi ya!");
    } finally {
      setSaving(false);
    }
  };

  const deleteEntry = async (id) => {
    await api.delete(`/calendar/${id}`);
    setEntries((prev) => prev.filter((e) => e.id !== id));
    if (editingId === id) { resetForm(); setShowForm(false); }
    toast.success("Rencana dihapus");
  };

  const nav = (dir) => setCursor((c) => {
    let m = c.m + dir, y = c.y;
    if (m < 0) { m = 11; y--; } if (m > 11) { m = 0; y++; }
    return { y, m };
  });

  const isToday = (d) => d && cursor.y === today.getFullYear() && cursor.m === today.getMonth() && d === today.getDate();

  return (
    <div>
      <div className="mb-6">
        <p className="text-sk-brand font-semibold uppercase tracking-wide text-xs mb-2">Jadwalkan</p>
        <h1 className="font-extrabold text-3xl sm:text-4xl tracking-tight text-sk-cream">Kalender Konten</h1>
        <p className="text-sk-muted mt-2 max-w-xl leading-relaxed">Rencanain ide & script kamu tiap tanggal. Bisa taruh lebih dari satu rencana per hari. Klik tanggal buat mulai.</p>
      </div>

      {pendingScript && (
        <div data-testid="pending-banner" className="mb-4 rounded-2xl border border-sk-brand/40 bg-sk-brand/10 p-4 flex items-center gap-3 text-sm">
          <CalendarPlus className="h-5 w-5 text-sk-brand shrink-0" />
          <span className="text-sk-cream flex-1">Draft siap dijadwalkan. Klik tanggal buat menempatkan script kamu.</span>
          <button onClick={clearPending} className="text-sk-muted hover:text-sk-cream"><X className="h-4 w-4" /></button>
        </div>
      )}

      <div className="rounded-2xl border border-sk-line bg-sk-surface p-4 sm:p-6 shadow-sm">
        <div className="flex items-center justify-between mb-5">
          <h2 className="font-extrabold text-lg sm:text-xl text-sk-cream tracking-tight">{MONTHS[cursor.m]} {cursor.y}</h2>
          <div className="flex gap-2">
            <button onClick={() => nav(-1)} data-testid="prev-month-btn" className="h-9 w-9 rounded-full border border-sk-line flex items-center justify-center text-sk-cream hover:border-sk-brand hover:text-sk-brand transition-colors"><ChevronLeft className="h-4 w-4" /></button>
            <button onClick={() => nav(1)} data-testid="next-month-btn" className="h-9 w-9 rounded-full border border-sk-line flex items-center justify-center text-sk-cream hover:border-sk-brand hover:text-sk-brand transition-colors"><ChevronRight className="h-4 w-4" /></button>
          </div>
        </div>

        <div className="grid grid-cols-7 gap-1.5 sm:gap-2 mb-2">
          {DAYS.map((d) => <div key={d} className="text-center text-xs font-semibold text-sk-muted py-1">{d}</div>)}
        </div>
        <div className="grid grid-cols-7 gap-1.5 sm:gap-2">
          {grid.map((d, i) => {
            if (!d) return <div key={i} />;
            const key = fmt(cursor.y, cursor.m, d);
            const count = (byDate[key] || []).length;
            return (
              <button
                key={i}
                data-testid={`calendar-day-${d}`}
                onClick={() => openDate(d)}
                className={`relative aspect-square rounded-xl border flex flex-col items-center justify-center text-sm font-medium transition-colors ${
                  isToday(d) ? "border-sk-brand text-sk-brand" : "border-sk-line text-sk-cream hover:border-sk-surface2 hover:bg-sk-bg"
                }`}
              >
                {d}
                {count > 0 && (
                  <span className="absolute bottom-1.5 flex gap-0.5" data-testid={`dot-${d}`}>
                    {Array.from({ length: Math.min(count, 3) }).map((_, k) => <span key={k} className="h-1.5 w-1.5 rounded-full bg-sk-brand" />)}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      <Sheet open={!!selDate} onOpenChange={(o) => { if (!o) { setSelDate(null); resetForm(); setShowForm(false); } }}>
        <SheetContent className="bg-sk-surface border-sk-line text-sk-cream w-full sm:max-w-md overflow-y-auto">
          <SheetHeader>
            <SheetTitle className="text-sk-cream font-extrabold tracking-tight">
              {selDate && new Date(selDate).toLocaleDateString("id-ID", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
            </SheetTitle>
            <SheetDescription className="text-sk-muted text-sm">Kelola ide & script konten untuk tanggal ini.</SheetDescription>
          </SheetHeader>

          <div className="mt-6 space-y-4">
            {/* Existing entries */}
            {dateEntries.length > 0 && (
              <div className="space-y-2.5" data-testid="calendar-entries">
                {dateEntries.map((e) => (
                  <div key={e.id} data-testid="calendar-entry" className="rounded-xl border border-sk-line bg-sk-bg p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <p className="font-bold text-sk-cream truncate">{e.idea || "Tanpa judul"}</p>
                        {e.notes && <p className="text-xs text-sk-muted mt-1 whitespace-pre-wrap line-clamp-4 leading-relaxed">{e.notes}</p>}
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        <button onClick={() => startEdit(e)} data-testid={`edit-entry-${e.id}`} className="p-1.5 text-sk-muted hover:text-sk-brand transition-colors"><Pencil className="h-3.5 w-3.5" /></button>
                        <button onClick={() => deleteEntry(e.id)} data-testid={`delete-entry-${e.id}`} className="p-1.5 text-sk-muted hover:text-sk-brand transition-colors"><Trash2 className="h-3.5 w-3.5" /></button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {!showForm ? (
              <button onClick={() => { resetForm(); setShowForm(true); }} data-testid="add-entry-btn" className="w-full flex items-center justify-center gap-2 rounded-full border border-sk-line py-2.5 text-sm font-semibold text-sk-cream hover:border-sk-brand hover:text-sk-brand transition-colors">
                <Plus className="h-4 w-4" /> Tambah Rencana
              </button>
            ) : (
              <div className="rounded-xl border border-sk-line bg-sk-bg p-4 space-y-4">
                <p className="text-sm font-bold text-sk-cream">{editingId ? "Ubah rencana" : "Rencana baru"}</p>
                <div>
                  <label className="block text-sm font-semibold text-sk-cream mb-2">Ide Konten</label>
                  <input
                    data-testid="calendar-idea-input"
                    value={idea}
                    onChange={(e) => setIdea(e.target.value)}
                    placeholder="cth: Reels tips hemat listrik"
                    className="w-full rounded-xl bg-sk-surface border border-sk-line px-4 py-3 text-sm text-sk-cream placeholder:text-sk-muted/60 focus:outline-none focus:ring-2 focus:ring-sk-brand focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-sk-cream mb-2">Script / Catatan</label>
                  <textarea
                    data-testid="calendar-notes-input"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    rows={8}
                    placeholder="Tempel script kamu atau tulis catatan di sini..."
                    className="w-full rounded-xl bg-sk-surface border border-sk-line px-4 py-3 text-sm text-sk-cream placeholder:text-sk-muted/60 resize-none focus:outline-none focus:ring-2 focus:ring-sk-brand focus:border-transparent leading-relaxed"
                  />
                </div>
                <div className="flex gap-2">
                  <button onClick={save} disabled={saving} data-testid="save-calendar-btn" className="flex-1 flex items-center justify-center gap-2 rounded-full bg-sk-brand text-sk-ink font-bold py-3 hover:bg-sk-brandDark transition-colors disabled:opacity-60">
                    <Save className="h-4 w-4" /> {saving ? "Menyimpan..." : "Simpan"}
                  </button>
                  {dateEntries.length > 0 && (
                    <button onClick={() => { resetForm(); setShowForm(false); }} className="rounded-full border border-sk-line px-4 text-sm font-semibold text-sk-muted hover:text-sk-cream transition-colors">Batal</button>
                  )}
                </div>
              </div>
            )}
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
